import { convertTypesToLetters } from "./categoryBucket";
import { isDirectSearchPreferences } from "./types";
import type { SaveUserPreferencesPayload, SearchPreferencesRead, UserPreferences } from "./types";

/**
 * Optimistically merges a save-preferences payload back into the cached
 * contract selected for the page. This keeps a bfcache-restored form aligned
 * with its save, even when no refetch occurs.
 */
export function mergePayloadIntoPreferences(
    cached: UserPreferences | null,
    payload: SaveUserPreferencesPayload,
): UserPreferences | null;
export function mergePayloadIntoPreferences(
    cached: SearchPreferencesRead | null,
    payload: SaveUserPreferencesPayload,
): SearchPreferencesRead | null;
export function mergePayloadIntoPreferences(
    cached: SearchPreferencesRead | null,
    payload: SaveUserPreferencesPayload,
): SearchPreferencesRead | null {
    if (cached === null) return cached;

    if (isDirectSearchPreferences(cached)) {
        return {
            ...cached,
            givenNameExactness:
                payload.givenNameExactness !== undefined ? payload.givenNameExactness : cached.givenNameExactness,
            surnameExactness:
                payload.surnameExactness !== undefined ? payload.surnameExactness : cached.surnameExactness,
            types: payload.types !== undefined ? payload.types : cached.types,
            collectionFocus: payload.collectionFocus !== undefined ? payload.collectionFocus : cached.collectionFocus,
            showMoreOptions: payload.showMoreOptions !== undefined ? payload.showMoreOptions : cached.showMoreOptions,
            matchAllTermsExactly:
                payload.matchAllTermsExactly !== undefined ? payload.matchAllTermsExactly : cached.matchAllTermsExactly,
            isSkipAssistPlacesModalPermanently:
                payload.isSkipAssistPlacesModalPermanently !== undefined
                    ? payload.isSkipAssistPlacesModalPermanently
                    : cached.isSkipAssistPlacesModalPermanently,
            unviewedFilterPopupShownCount:
                payload.unviewedFilterPopupShownCount !== undefined
                    ? payload.unviewedFilterPopupShownCount
                    : cached.unviewedFilterPopupShownCount,
            showUnviewedRecordsOnly:
                payload.showUnviewedRecordsOnly !== undefined
                    ? payload.showUnviewedRecordsOnly
                    : cached.showUnviewedRecordsOnly,
        };
    }

    const prefs: UserPreferences = { ...cached, UserPreferences: { ...cached.UserPreferences } };

    if (payload.givenNameExactness) {
        prefs.UserPreferences.FirstNameExactness = {
            Exact: payload.givenNameExactness.isExact,
            Phonetic: payload.givenNameExactness.flags.phonetic,
            Initials: payload.givenNameExactness.flags.initials,
            Similar: payload.givenNameExactness.flags.similar,
        };
    }

    if (payload.surnameExactness) {
        prefs.UserPreferences.LastNameExactness = {
            Exact: payload.surnameExactness.isExact,
            Phonetic: payload.surnameExactness.flags.phonetic,
            Similar: payload.surnameExactness.flags.similar,
            Soundex: payload.surnameExactness.flags.soundex,
        };
    }

    if (payload.types) {
        prefs.UserPreferences.CategoryBucket = convertTypesToLetters(payload.types);
    }

    if (payload.legacyCollectionFocus !== undefined || payload.collectionFocus !== undefined) {
        prefs.UserPreferences.CollectionFocus = payload.legacyCollectionFocus ?? payload.collectionFocus!;
    }

    if (payload.showUnviewedRecordsOnly !== undefined) {
        prefs.UserPreferences.ShowUnviewedRecordsOnly = payload.showUnviewedRecordsOnly;
    }

    if (payload.showMoreOptions !== undefined) {
        prefs.UserPreferences.ShowMoreOptions = payload.showMoreOptions;
    }

    if (payload.matchAllTermsExactly !== undefined) {
        prefs.UserPreferences.MatchAllTermsExactly = payload.matchAllTermsExactly;
    }

    if (payload.isSkipAssistPlacesModalPermanently !== undefined) {
        prefs.UserPreferences.IsSkipAssistPlacesModalPermanently = payload.isSkipAssistPlacesModalPermanently;
    }

    return prefs;
}
